import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Plus, Search, ShoppingCart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PaymentBadge, Loading, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OrderFormDialog } from "./OrderForm";

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const params = status !== "all" ? { status } : {};
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  if (orders === null) return <Loading />;

  const filtered = orders.filter((o) =>
    !q || o.order_id.toLowerCase().includes(q.toLowerCase()) ||
    (o.business_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (o.contact_person || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">{isAdmin ? "Orders" : "My Orders"}</h1>
          <p className="text-sm text-slate-500 mt-1">{isAdmin ? "Manage all customer orders and campaigns." : "Orders assigned to you."}</p>
        </div>
        {isAdmin && (
          <Button data-testid="create-order-button" onClick={() => setOpen(true)} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
            <Plus className="h-4 w-4 mr-1.5" /> New Order
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by order ID, business, contact…" className="pl-9 bg-white" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-white" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="No orders found" sub={isAdmin ? "Create a new order to get started." : "You have no assigned orders."} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Timeline</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  {isAdmin && <TableHead>Staff</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`}
                    className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/${o.order_id}`)}>
                    <TableCell className="font-mono font-medium text-slate-900">{o.order_id}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{o.business_name}</div>
                      <div className="text-xs text-slate-500">{o.contact_person}</div>
                    </TableCell>
                    <TableCell className="text-slate-600">{o.plan_name || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{formatDate(o.start_date)} → {formatDate(o.end_date)}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell><PaymentBadge status={o.payment_status} /></TableCell>
                    {isAdmin && <TableCell className="text-slate-600">{o.assigned_staff_name || "—"}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {isAdmin && <OrderFormDialog open={open} onOpenChange={setOpen} onSaved={load} />}
    </div>
  );
}
